import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function AdminRegistrationStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("today"); // today, week, month, year, custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      let params = {};
      if (period === "custom") {
        if (!startDate || !endDate) {
          setLoading(false);
          return; 
        }
        params = { startDate, endDate };
      } else {
        params = { period };
      }

      const res = await axiosInstance.get("/api/reports/admin/registration-stats", { params });
      
      const rawLabels = res.data?.labels || [];
      const rawReg = res.data?.datasets?.registrations || [];
      const rawUpgrades = res.data?.datasets?.entrepreneurUpgrades || [];

      const chartData = rawLabels.map((lbl, idx) => ({
        name: lbl,
        Registrations: rawReg[idx] || 0,
        Upgrades: rawUpgrades[idx] || 0,
      }));

      setData({
        chart: chartData,
        summary: res.data?.summary || { totalRegistrations: 0, totalEntrepreneurUpgrades: 0 },
      });
    } catch (err) {
      setError("Failed to fetch statistics. Please check API or try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSearch = () => {
    if (period !== "custom") setPeriod("custom");
    else fetchStats();
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.h1}>Today's Registration Stats</div>
          <div style={styles.sub}>Detailed report of user registrations and upgrades</div>
        </div>
        <button style={styles.btn} onClick={() => navigate("/admin/dashboard")}>
          ← Dashboard
        </button>
      </div>

      <div style={styles.card}>
        <div style={styles.filtersWrapper}>
          <div style={styles.filterBtns}>
            {["today", "week", "month", "year"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{ ...styles.filterBtn, ...(period === p ? styles.activeFilter : {}) }}
              >
                {p.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => setPeriod("custom")}
              style={{ ...styles.filterBtn, ...(period === "custom" ? styles.activeFilter : {}) }}
            >
              CUSTOM
            </button>
          </div>

          {period === "custom" && (
            <div style={styles.customDateLine}>
              <input
                type="date"
                style={styles.dateInput}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span style={{ fontWeight: 900 }}>TO</span>
              <input
                type="date"
                style={styles.dateInput}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <button style={styles.searchBtn} onClick={handleCustomSearch}>
                Apply
              </button>
            </div>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.muted}>Fetching newest data...</div>
        ) : !data ? (
          <div style={styles.muted}>No stats available for this period.</div>
        ) : (
          <>
            <div style={styles.summaryGrid}>
              <div style={{ ...styles.summaryBox, background: "#ecfdf5", border: "1px solid #6ee7b7" }}>
                <div style={styles.summaryLabel}>Total Registrations</div>
                <div style={{ ...styles.summaryValue, color: "#059669" }}>
                  {data.summary.totalRegistrations}
                </div>
                <div style={styles.boxHint}>In {period} period</div>
              </div>
              <div style={{ ...styles.summaryBox, background: "#fff7ed", border: "1px solid #fdba74" }}>
                <div style={styles.summaryLabel}>Entrepreneur Upgrades</div>
                <div style={{ ...styles.summaryValue, color: "#ea580c" }}>
                  {data.summary.totalEntrepreneurUpgrades}
                </div>
                <div style={styles.boxHint}>In {period} period</div>
              </div>
            </div>

            <div style={styles.chartWrapper}>
              <h3 style={{ marginTop: 0, marginBottom: 20 }}>
                Registration Trends
                <span style={styles.dateRangeBadge}>
                   {data.summary.dateRange?.from || "-"} to {data.summary.dateRange?.to || "-"}
                </span>
              </h3>
              
              {data.chart.length === 0 ? (
                <div style={styles.muted}>No timeline data for this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={data.chart} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <Tooltip 
                      cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                    <Bar 
                      name="Registrations"
                      dataKey="Registrations" 
                      fill="#10b981" 
                      radius={[6, 6, 0, 0]} 
                      barSize={Math.max(10, 120 / data.chart.length)}
                    />
                    <Bar 
                      name="Upgrades"
                      dataKey="Upgrades" 
                      fill="#f97316" 
                      radius={[6, 6, 0, 0]} 
                      barSize={Math.max(10, 120 / data.chart.length)}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 22, minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 },
  h1: { fontSize: 26, fontWeight: 900, color: "#0f172a" },
  sub: { fontSize: 13, color: "#64748b", marginTop: 4 },
  btn: { padding: "9px 18px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
  card: { background: "#fff", padding: 28, borderRadius: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9" },
  filtersWrapper: { display: "flex", flexDirection: "column", gap: 16, marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid #f1f5f9" },
  filterBtns: { display: "flex", gap: 8, flexWrap: "wrap" },
  filterBtn: { padding: "7px 15px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12, color: "#64748b", transition: "0.2s" },
  activeFilter: { background: "#0f172a", color: "#fff", borderColor: "#0f172a" },
  customDateLine: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", background: "#f8fafc", padding: "12px 16px", borderRadius: 14, border: "1px solid #e2e8f0" },
  dateInput: { padding: "8px 12px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", fontWeight: 700, fontSize: 13 },
  searchBtn: { padding: "8px 20px", borderRadius: 10, background: "#10b981", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 32 },
  summaryBox: { padding: 24, borderRadius: 18 },
  summaryLabel: { fontSize: 13, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 },
  summaryValue: { fontSize: 38, fontWeight: 950 },
  boxHint: { marginTop: 6, fontSize: 12, opacity: 0.7, fontWeight: 600 },
  chartWrapper: { paddingTop: 10 },
  dateRangeBadge: { marginLeft: 12, fontSize: 11, padding: "4px 12px", background: "#f1f5f9", borderRadius: 20, fontWeight: 600, color: "#64748b" },
  error: { padding: 14, background: "#fef2f2", color: "#991b1b", borderRadius: 12, marginBottom: 20, fontWeight: 700, fontSize: 13 },
  muted: { color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "40px 0" },
};
